// Boomin' — Top / Discover screen (/discover, /area/:name)

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  BRAND,
  BRAND_SOFT,
  ACCENT,
  PictX,
  PictName,
  BottomNavX,
  AppHeader,
  AppFooter,
  useBoominFx,
} from "./shared";
import { OnboardingOverlay } from "./OnboardingOverlay";
import {
  PRESET_AREAS,
  Position,
  RedesignCategory,
  categoryLabel,
  couponHubArea,
  couponHubLat,
  couponHubLng,
  couponHubName,
  couponTag,
  fetchHubsByLatLng,
  fetchTop,
  forwardGeocode,
  formatExpiry,
  formatWalk,
  getCurrentPosition,
  haversineKm,
  inferCategory,
  lookupPresetArea,
  reverseGeocode,
} from "./api";
import { Coupon, Hub } from "../../Types/App";

const brand = BRAND;
const brandSoft = BRAND_SOFT;
const accent = ACCENT;

const ONBOARD_KEY = "boomin_seen";
const NEARBY_RADIUS_KM = 1.0; // ~徒歩12分圏

type CategoryOption = {
  id: RedesignCategory | "all";
  label: string;
  icon: PictName;
};

const CATEGORIES: CategoryOption[] = [
  { id: "all", label: "すべて", icon: "sparkle" },
  { id: "cafe", label: "カフェ", icon: "cafe" },
  { id: "meal", label: "ごはん", icon: "ramen" },
  { id: "sweet", label: "スイーツ", icon: "sweet" },
  { id: "bar", label: "バー", icon: "bar" },
  { id: "shop", label: "ショップ", icon: "shop" },
  { id: "salon", label: "サロン", icon: "salon" },
  { id: "activity", label: "体験", icon: "activity" },
];

const isCategory = (v: string | null): v is RedesignCategory | "all" =>
  v != null && CATEGORIES.some((c) => c.id === v);

type DisplayCoupon = {
  id: number;
  shop: string;
  title: string;
  area: string;
  walk: string;
  until: string;
  img: string;
  tag: string;
  cat: RedesignCategory;
};

const couponToDisplay = (
  c: Coupon,
  origin: Position | null,
  hub?: Hub | null
): DisplayCoupon => {
  const lat = couponHubLat(c, hub);
  const lng = couponHubLng(c, hub);
  const km = origin && lat && lng ? haversineKm(origin, { lat, lng }) : null;
  return {
    id: c.id,
    shop: couponHubName(c, hub),
    title: c.title,
    area: couponHubArea(c, hub),
    walk: km !== null ? formatWalk(km) : "",
    until: formatExpiry(c.period_end),
    img: c.image || hub?.image || "",
    tag: couponTag(c.period_end),
    cat: inferCategory(c, hub || null),
  };
};

export const TopScreen: React.FC = () => {
  useBoominFx();
  const params = useParams<{ name?: string; cat?: string }>();
  const areaName = params?.name;
  const catParam = params?.cat;
  const [showOnboard, setShowOnboard] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(ONBOARD_KEY)) setShowOnboard(true);
    } catch {
      /* localStorage may be disabled */
    }
  }, []);

  const dismissOnboard = () => {
    try {
      localStorage.setItem(ONBOARD_KEY, "1");
    } catch {
      /* ignore */
    }
    setShowOnboard(false);
  };

  // SSR (server/app/handlers/public/home.go) と同じ文言を組み立てる
  const catPart =
    isCategory(catParam ?? null) && catParam !== "all"
      ? `${categoryLabel(catParam as RedesignCategory)}の`
      : "";
  const title = areaName
    ? `いま、${areaName}で使える${catPart}クーポンが見つかる｜ローカルクーポンのBoomin'（ブーミン）`
    : `いま、このあたりで使える${catPart}クーポンが見つかる｜ローカルクーポンのBoomin'（ブーミン）`;
  const description = areaName
    ? `${areaName}周辺で、いま使える${catPart}クーポンを徒歩圏内で見つけよう。`
    : `位置情報を許可すると、徒歩10分以内の${catPart}クーポンが一覧で見つかります。`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
      </Helmet>
      <AppHeader />
      <TopScreenContent areaName={areaName} catParam={catParam} />
      <AppFooter withBottomNav />
      <BottomNavX active="home" brand={brand} />
      {showOnboard && <OnboardingOverlay onDismiss={dismissOnboard} />}
    </>
  );
};

type TopScreenContentProps = {
  areaName?: string;
  catParam?: string;
};

export const TopScreenContent: React.FC<TopScreenContentProps> = ({ areaName, catParam }) => {
  const text = "#1A1A1A";
  const sub = "#777";
  const bg = "#FFFBF7";

  const location = useLocation();
  const history = useHistory();

  // The selected category lives in the static path segment
  // (/discover/:cat or /area/:name/:cat) for SEO. We still honor the legacy
  // ?cat= query param so old links keep resolving.
  const selectedCat = useMemo<RedesignCategory | "all">(() => {
    if (isCategory(catParam ?? null)) return catParam as RedesignCategory | "all";
    const q = new URLSearchParams(location.search).get("cat");
    return isCategory(q) ? q : "all";
  }, [catParam, location.search]);

  // Selecting a category navigates to its static URL. "all" drops the segment.
  const categoryBase = areaName ? `/area/${encodeURIComponent(areaName)}` : "/discover";
  const navigateCategory = useCallback(
    (cat: RedesignCategory | "all") => {
      history.push(cat === "all" ? categoryBase : `${categoryBase}/${cat}`);
    },
    [history, categoryBase]
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [origin, setOrigin] = useState<Position | null>(null);
  const [areaLabel, setAreaLabel] = useState<string>(areaName || "");
  const [allCoupons, setAllCoupons] = useState<Coupon[]>([]);
  const [nearbyHubs, setNearbyHubs] = useState<Hub[]>([]);
  const [areaSheetOpen, setAreaSheetOpen] = useState(false);

  // Flatten hub.coupons into display rows, paired with their hub for address info.
  const nearbyDisplay = useMemo<DisplayCoupon[]>(() => {
    if (nearbyHubs.length === 0) return [];
    const rows: DisplayCoupon[] = [];
    nearbyHubs.forEach((h) => {
      (h.coupons || []).forEach((c) => rows.push(couponToDisplay(c, origin, h)));
    });
    return rows
      .filter((r) => r.title) // skip rows we couldn't render
      .sort((a, b) => {
        // sort by walking distance when available, urgent tag first
        if (a.tag === "本日まで" && b.tag !== "本日まで") return -1;
        if (b.tag === "本日まで" && a.tag !== "本日まで") return 1;
        return 0;
      });
  }, [nearbyHubs, origin]);

  const featuredCandidates = useMemo<DisplayCoupon[]>(() => {
    // Prefer the closest nearby coupon for the hero; fall back to /top coupons.
    if (nearbyDisplay.length > 0) return nearbyDisplay;
    return allCoupons.map((c) => couponToDisplay(c, origin, null));
  }, [nearbyDisplay, allCoupons, origin]);

  // Apply the selected-category filter after we've built the full candidate list
  // — so when the filter empties out, we can show a category-specific empty state.
  const filteredCandidates = useMemo<DisplayCoupon[]>(() => {
    if (selectedCat === "all") return featuredCandidates;
    return featuredCandidates.filter((c) => c.cat === selectedCat);
  }, [featuredCandidates, selectedCat]);

  const featured = filteredCandidates[0];
  const list = filteredCandidates.slice(1, 6);
  const nearbyCount =
    selectedCat === "all"
      ? nearbyDisplay.length || Math.min(allCoupons.length, 99)
      : filteredCandidates.length;

  const loadForOrigin = useCallback(async (here: Position, label?: string) => {
    setLoading(true);
    setError("");
    try {
      setOrigin(here);
      if (label) setAreaLabel(label);
      else {
        reverseGeocode(here.lat, here.lng).then((l) => {
          if (l) setAreaLabel(l);
        });
      }
      const hubs = await fetchHubsByLatLng(here.lat, here.lng, NEARBY_RADIUS_KM);
      setNearbyHubs(hubs || []);
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Always fetch the public /top payload first (works without geolocation).
      const top = await fetchTop();
      setAllCoupons(top.coupons || []);

      // If we have an :area name in the URL, prefer that over geolocation.
      if (areaName) {
        const preset = lookupPresetArea(areaName);
        if (preset) {
          await loadForOrigin(preset, areaName);
          return;
        }
        const geo = await forwardGeocode(areaName);
        if (geo) {
          await loadForOrigin(geo, areaName);
          return;
        }
        // Fallback: treat as "current location" if we couldn't resolve the name.
      }

      // Use browser geolocation when granted; fall back to /top's IP-based lat/lng.
      let here: Position = { lat: top.lat, lng: top.lng };
      let geolocated = false;
      try {
        const pos = await getCurrentPosition();
        if (pos.lat && pos.lng) {
          here = pos;
          geolocated = true;
        }
      } catch {
        // user denied or unsupported — keep IP-based fallback
      }
      // Only reverse-geocode when we have the user's real location. On the
      // IP/default fallback, the coordinates resolve to a fixed spot
      // (渋谷区・代官山町), which is misleading as "今いるエリア" — show a
      // neutral placeholder prompting the user to pick an area instead.
      await loadForOrigin(here, geolocated ? undefined : "エリアを選択");
    } catch (e: any) {
      setError(typeof e === "string" ? e : e?.message || "読み込みに失敗しました");
    } finally {
      setLoading(false);
    }
  }, [areaName, loadForOrigin]);

  useEffect(() => {
    load();
  }, [load]);

  // Area selection is URL-driven, mirroring category selection above: the path
  // is the single source of truth so the heading, category base, and any later
  // navigation all stay consistent with the chosen area. (Previously this only
  // mutated local state, so on an /area/:name page the stale URL would revert
  // the change as soon as the user touched a category or reloaded.)
  const catSuffix = selectedCat === "all" ? "" : `/${selectedCat}`;

  const onPickPreset = (preset: { name: string; lat: number; lng: number }) => {
    setAreaSheetOpen(false);
    history.push(`/area/${encodeURIComponent(preset.name)}${catSuffix}`);
  };

  const onUseMyLocation = async () => {
    setAreaSheetOpen(false);
    // "現在地から探す": drop the :area segment so load() re-runs geolocation.
    if (areaName) {
      history.push(`/discover${catSuffix}`);
      return;
    }
    // Already on /discover — re-request geolocation directly.
    try {
      const pos = await getCurrentPosition();
      await loadForOrigin(pos);
      const label = await reverseGeocode(pos.lat, pos.lng);
      if (label) setAreaLabel(label);
    } catch {
      /* geolocation denied — keep current origin */
    }
  };

  return (
    <div
      style={{
        background: bg,
        minHeight: "100vh",
        paddingBottom: 0,
        color: text,
      }}
    >
      {/* Area selector */}
      <div style={{ padding: "16px 20px 8px" }}>
        <button
          type="button"
          onClick={() => setAreaSheetOpen(true)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            background: "#fff",
            border: "1px solid #F0E6DC",
            borderRadius: 14,
            padding: "10px 12px",
            cursor: "pointer",
            fontFamily: "inherit",
            color: text,
            width: "100%",
            textAlign: "left",
          }}
        >
          <span
            style={{
              width: 36,
              height: 36,
              borderRadius: 10,
              background: brandSoft,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PictX name="pin" size={18} stroke={brand} strokeWidth={2} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                fontSize: 10,
                color: sub,
                letterSpacing: "0.06em",
                fontWeight: 700,
                display: "block",
              }}
            >
              {areaName ? "エリア" : "今いるエリア"}
            </span>
            <span
              style={{
                fontSize: 15,
                fontWeight: 800,
                lineHeight: 1.2,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {areaLabel || "現在地を取得中…"}
            </span>
          </span>
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              color: brand,
              background: brandSoft,
              padding: "5px 10px",
              borderRadius: 999,
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              flexShrink: 0,
            }}
          >
            変更
            <PictX name="chevron" size={11} stroke={brand} strokeWidth={2.4} />
          </span>
        </button>

        <div
          style={{
            marginTop: 10,
            display: "flex",
            gap: 8,
            justifyContent: "flex-end",
          }}
        >
          <a aria-label="マップを見る" href="/map" style={iconBtn}>
            <PictX name="map" size={18} stroke={text} />
          </a>
          <a aria-label="マイクーポン" href="/wallet" style={iconBtn}>
            <PictX name="ticket" size={18} stroke={text} />
          </a>
        </div>
      </div>

      {/* Hero */}
      <div style={{ padding: "4px 20px 0" }}>
        <div
          style={{
            position: "relative",
            borderRadius: 22,
            overflow: "hidden",
            background: brandSoft,
            padding: "20px 18px 22px",
          }}
        >
          <ConfettiRibbons color={brand} alt={accent} />
          <div style={{ position: "relative" }}>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                background: "#fff",
                borderRadius: 999,
                padding: "5px 11px 5px 9px",
                fontSize: 11,
                fontWeight: 700,
                color: brand,
                boxShadow: "0 1px 0 rgba(0,0,0,0.04)",
              }}
            >
              <PictX name="walk" size={14} stroke={brand} strokeWidth={2} />
              寄り道、はじめよう
            </div>
            <h1
              style={{
                fontSize: 28,
                fontWeight: 800,
                lineHeight: 1.25,
                margin: "14px 0 6px",
                letterSpacing: "0.01em",
              }}
            >
              いま、
              <br />
              <span
                style={{
                  background: `linear-gradient(transparent 60%, ${brand}30 60%)`,
                  paddingRight: 4,
                }}
              >
                このあたりで使える
              </span>
              <br />
              クーポン<span style={{ color: brand }}>。</span>
            </h1>
            <div style={{ fontSize: 12, color: "#5A4A3F", lineHeight: 1.6 }}>
              {loading
                ? "読み込み中…"
                : nearbyCount > 0
                ? <>
                    {selectedCat === "all" ? "徒歩圏内に" : `${categoryLabel(selectedCat)}が`}{" "}
                    <b style={{ color: brand, fontSize: 14 }}>{nearbyCount}</b>{" "}
                    件あります
                  </>
                : "近くにはまだクーポンがありません。マップで広く探してみよう。"}
            </div>
          </div>
        </div>
      </div>

      {/* Categories — tap to filter */}
      <div style={{ padding: "20px 0 0" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 10,
            padding: "0 20px",
          }}
        >
          {CATEGORIES.map((c) => {
            const on = selectedCat === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => navigateCategory(c.id)}
                style={{
                  background: on ? brand : "#fff",
                  border: on ? `1px solid ${brand}` : "1px solid #F0E6DC",
                  borderRadius: 16,
                  padding: "12px 4px 10px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                  cursor: "pointer",
                  color: on ? "#fff" : text,
                  fontFamily: "inherit",
                }}
              >
                <div
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 12,
                    background: on ? "rgba(255,255,255,0.2)" : brandSoft,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <PictX
                    name={c.icon}
                    size={22}
                    stroke={on ? "#fff" : brand}
                    strokeWidth={1.7}
                  />
                </div>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: on ? "#fff" : text,
                  }}
                >
                  {c.label}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Featured */}
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : loading && !featured ? (
        <LoadingState />
      ) : featured ? (
        <div style={{ padding: "28px 20px 0" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <PictX name="flame" size={18} stroke={brand} strokeWidth={1.8} />
              <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>
                今だけのクーポン
              </h2>
            </div>
            <a href="/map" style={linkBtn}>
              地図で見る{" "}
              <PictX name="chevron" size={12} stroke={brand} strokeWidth={2.2} />
            </a>
          </div>
          <FeaturedCard item={featured} />
        </div>
      ) : (
        <CategoryEmptyState
          category={selectedCat}
          onClear={() => navigateCategory("all")}
        />
      )}

      {/* Nearby list */}
      {list.length > 0 && (
        <div style={{ padding: "28px 20px 0" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: 12,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <PictX name="pin" size={18} stroke={text} strokeWidth={1.8} />
              <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0 }}>近くで使える</h2>
            </div>
            <a href="/map" style={linkBtn}>
              地図で見る{" "}
              <PictX name="chevron" size={12} stroke={brand} strokeWidth={2.2} />
            </a>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {list.map((c) => (
              <NearbyRow key={c.id} item={c} />
            ))}
          </div>
        </div>
      )}

      {areaSheetOpen && (
        <AreaSheet
          current={areaLabel}
          onClose={() => setAreaSheetOpen(false)}
          onPickPreset={onPickPreset}
          onUseMyLocation={onUseMyLocation}
        />
      )}
    </div>
  );
};

const iconBtn: React.CSSProperties = {
  width: 36,
  height: 36,
  borderRadius: 999,
  background: "#fff",
  border: "1px solid #F0E6DC",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  textDecoration: "none",
  color: "#1A1A1A",
};

const linkBtn: React.CSSProperties = {
  background: "transparent",
  border: 0,
  padding: 0,
  display: "inline-flex",
  alignItems: "center",
  gap: 2,
  fontSize: 12,
  fontWeight: 700,
  color: "#777",
  cursor: "pointer",
  textDecoration: "none",
};

const ConfettiRibbons: React.FC<{ color: string; alt: string }> = ({ color, alt }) => (
  <svg
    width="100%"
    height="100%"
    viewBox="0 0 360 200"
    preserveAspectRatio="none"
    style={{ position: "absolute", inset: 0 }}
  >
    <defs>
      <pattern id="dots" x="0" y="0" width="18" height="18" patternUnits="userSpaceOnUse">
        <circle cx="2" cy="2" r="1" fill={color} opacity="0.18" />
      </pattern>
    </defs>
    <rect width="360" height="200" fill="url(#dots)" />
    <path
      d="M-10 130 Q 60 90, 130 130 T 270 130 T 410 130 L 410 145 Q 340 105 270 145 T 130 145 T -10 145 Z"
      fill={color}
      opacity="0.9"
    />
    <path
      d="M-10 150 Q 60 110, 130 150 T 270 150 T 410 150 L 410 162 Q 340 122 270 162 T 130 162 T -10 162 Z"
      fill={alt}
      opacity="0.7"
    />
    <g fill={color}>
      <circle cx="40" cy="40" r="3" />
      <circle cx="320" cy="60" r="4" />
      <circle cx="270" cy="30" r="2" />
      <path d="M90 25 l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" opacity="0.8" />
      <path d="M300 100 l2 6 6 2-6 2-2 6-2-6-6-2 6-2z" opacity="0.6" />
    </g>
  </svg>
);

const FeaturedCard: React.FC<{ item: DisplayCoupon }> = ({ item }) => (
  <a
    href={`/c/${item.id}`}
    style={{
      display: "block",
      background: "#fff",
      borderRadius: 22,
      overflow: "hidden",
      border: "1px solid #F0E6DC",
      textDecoration: "none",
      color: "inherit",
    }}
  >
    <div style={{ position: "relative", height: 180, overflow: "hidden", background: brandSoft }}>
      {item.img ? (
        <img
          src={item.img}
          alt={item.shop}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictX name="ticket" size={64} stroke={brand} strokeWidth={1.4} />
        </div>
      )}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          background: brand,
          color: "#fff",
          fontSize: 11,
          fontWeight: 800,
          padding: "5px 10px",
          borderRadius: 999,
          boxShadow: `0 2px 8px ${brand}66`,
        }}
      >
        {item.tag}
      </div>
    </div>
    <div style={{ padding: "14px 16px 16px" }}>
      <div style={{ fontSize: 11, color: "#777", fontWeight: 600, marginBottom: 4 }}>
        {item.shop}
      </div>
      <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.3, marginBottom: 10 }}>
        {item.title}
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          fontSize: 11,
          color: "#555",
        }}
      >
        {item.walk && (
          <>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
              <PictX name="walk" size={13} stroke="#777" strokeWidth={1.8} />
              {item.walk}
            </span>
            <span style={{ width: 1, height: 10, background: "#E5E0D8" }} />
          </>
        )}
        {item.until && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
            <PictX name="clock" size={13} stroke="#777" strokeWidth={1.8} />
            {item.until}
          </span>
        )}
      </div>
    </div>
  </a>
);

const NearbyRow: React.FC<{ item: DisplayCoupon }> = ({ item }) => (
  <a
    href={`/c/${item.id}`}
    style={{
      display: "flex",
      gap: 12,
      background: "#fff",
      borderRadius: 16,
      padding: 10,
      border: "1px solid #F0E6DC",
      textDecoration: "none",
      color: "inherit",
      alignItems: "stretch",
    }}
  >
    <div
      style={{
        width: 84,
        height: 84,
        borderRadius: 12,
        overflow: "hidden",
        flexShrink: 0,
        position: "relative",
        background: brandSoft,
      }}
    >
      {item.img ? (
        <img
          src={item.img}
          alt=""
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictX name="ticket" size={32} stroke={brand} strokeWidth={1.4} />
        </div>
      )}
    </div>
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minWidth: 0,
      }}
    >
      <div>
        <div
          style={{
            display: "inline-block",
            fontSize: 10,
            fontWeight: 700,
            color: brand,
            background: brandSoft,
            padding: "2px 7px",
            borderRadius: 999,
            marginBottom: 4,
          }}
        >
          {item.tag}
        </div>
        <div
          style={{
            fontSize: 14,
            fontWeight: 700,
            lineHeight: 1.3,
            marginBottom: 2,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {item.title}
        </div>
        <div style={{ fontSize: 11, color: "#777" }}>{item.shop}</div>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 10,
          color: "#888",
        }}
      >
        {item.walk && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <PictX name="walk" size={11} stroke="#888" strokeWidth={1.8} />
            {item.walk}
          </span>
        )}
        {item.walk && item.until && <span>•</span>}
        {item.until && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
            <PictX name="clock" size={11} stroke="#888" strokeWidth={1.8} />
            {item.until}
          </span>
        )}
      </div>
    </div>
  </a>
);

const LoadingState: React.FC = () => (
  <div style={{ padding: "28px 20px 0" }}>
    <div
      style={{
        height: 220,
        background: "#fff",
        border: "1px solid #F0E6DC",
        borderRadius: 22,
      }}
    />
  </div>
);

const CategoryEmptyState: React.FC<{
  category: RedesignCategory | "all";
  onClear: () => void;
}> = ({ category, onClear }) => (
  <div
    style={{
      margin: "28px 20px 0",
      padding: "32px 20px",
      background: "#fff",
      border: "1px dashed #F0E6DC",
      borderRadius: 18,
      textAlign: "center",
      color: "#777",
      fontSize: 13,
      lineHeight: 1.7,
    }}
  >
    {category === "all" ? (
      <>
        まだ近くのクーポンが見つかりません。
        <br />
        マップで広く探してみよう 🍊
      </>
    ) : (
      <>
        近くに「{categoryLabel(category)}」のクーポンが見つかりません。
        <br />
        <button
          type="button"
          onClick={onClear}
          style={{
            marginTop: 12,
            background: brand,
            color: "#fff",
            border: 0,
            borderRadius: 999,
            padding: "8px 16px",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: "inherit",
          }}
        >
          すべてのクーポンを見る
        </button>
      </>
    )}
  </div>
);

const ErrorState: React.FC<{ message: string; onRetry: () => void }> = ({ message, onRetry }) => (
  <div
    style={{
      margin: "28px 20px 0",
      padding: "20px 20px",
      background: "#fff",
      border: "1px solid #FFDAD0",
      borderRadius: 18,
      color: "#A6321E",
      fontSize: 13,
      lineHeight: 1.6,
    }}
  >
    <div style={{ marginBottom: 10 }}>クーポンを取得できませんでした。{message}</div>
    <button
      onClick={onRetry}
      style={{
        background: brand,
        color: "#fff",
        border: 0,
        borderRadius: 999,
        padding: "8px 16px",
        fontWeight: 700,
        cursor: "pointer",
        fontFamily: "inherit",
      }}
    >
      もう一度
    </button>
  </div>
);

const AreaSheet: React.FC<{
  current: string;
  onClose: () => void;
  onPickPreset: (preset: { name: string; lat: number; lng: number }) => void;
  onUseMyLocation: () => void;
}> = ({ current, onClose, onPickPreset, onUseMyLocation }) => (
  <div
    role="dialog"
    aria-modal="true"
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(20, 16, 12, 0.45)",
      zIndex: 40,
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
    }}
    onClick={onClose}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "100%",
        maxWidth: 640,
        background: "#FFFBF7",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        padding: "18px 20px calc(env(safe-area-inset-bottom, 0px) + 24px)",
      }}
    >
      <div
        style={{
          width: 36,
          height: 4,
          background: "#E5DBD0",
          borderRadius: 999,
          margin: "0 auto 14px",
        }}
      />
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 14,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: "#888",
              fontWeight: 700,
              letterSpacing: "0.08em",
            }}
          >
            エリアを選ぶ
          </div>
          <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2 }}>
            {current || "現在地"}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="閉じる"
          style={{
            background: "transparent",
            border: 0,
            cursor: "pointer",
            color: "#666",
            padding: 4,
          }}
        >
          <PictX name="close" size={20} stroke="#666" strokeWidth={1.8} />
        </button>
      </div>

      <button
        type="button"
        onClick={onUseMyLocation}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          gap: 10,
          background: brand,
          color: "#fff",
          border: 0,
          borderRadius: 14,
          padding: "12px 14px",
          fontWeight: 800,
          fontSize: 14,
          cursor: "pointer",
          fontFamily: "inherit",
          marginBottom: 12,
        }}
      >
        <PictX name="locate" size={18} stroke="#fff" strokeWidth={2} />
        現在地から探す
      </button>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {PRESET_AREAS.map((preset) => (
          <button
            key={preset.name}
            type="button"
            onClick={() => onPickPreset(preset)}
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 10,
              background: "#fff",
              border: "1px solid #F0E6DC",
              borderRadius: 14,
              padding: "12px 14px",
              cursor: "pointer",
              color: "#1A1A1A",
              fontFamily: "inherit",
              textAlign: "left",
            }}
          >
            <PictX name="pin" size={16} stroke={brand} strokeWidth={2} />
            <span style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>
              {preset.name}
            </span>
            <PictX name="chevron" size={14} stroke="#999" strokeWidth={2} />
          </button>
        ))}
      </div>
    </div>
  </div>
);

export default TopScreen;
